'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import BookingSummary from '@/components/booking-summary/summary';
import FareSummary from '@/components/fare-summary/fare-summary';
import Seat from '@/components/add-ons/seat';
import Meal from '@/components/add-ons/meal';
import Baggage from '@/components/add-ons/baggage';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSeatMapMutation } from '@flysync/shared-redux/services/booking';
import {
  updateFareDetails,
  updatePassenger,
  updateReviewData,
  updateSelectedSSR,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useRouter } from '@/lib/navigation';
import { useToast } from '@/hooks/use-toast';
import { config, getTemplateAssetPath } from '@/config';
import {
  calculatePassengerAmounts,
  extractPassengerSSRItems,
} from '@/lib/passenger-amounts';
const SsrDetails = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('meal');
  const [ssrInfoData, setSsrInfoData] = useState<any>([]);
  const [seatMap, setSeatMap] = useState<any>({ sInfo: [], sData: {} });
  const [meal, setMeal] = useState<any>([]);
  const [baggage, setBaggage] = useState<any>([]);
  const [activeIndexes, setActiveIndexes] = useState<any>({
    i: 0,
    index: 0,
    paxIndex: 0,
  });

  const { toast } = useToast();

  const [passengerData, setPassengerData] = useState([]);

  const [seatData, setSeatData] = useState<any>([]);

  const [getSeatMap] = useGetSeatMapMutation({
    fixedCacheKey: 'seat-map',
  });

  const flightSearch = useSelector((state: any) => state.flightSearch);

  const router = useRouter();

  const [isApiCall, setIsApiCall] = useState(false);

  const Loader = getTemplateAssetPath('loader');

  const selectedBucket = useSelector(
    (state: any) => state.flightSearch.fareDetails,
  );
  const selectedSSR = useSelector(
    (state: any) => state.flightSearch.selectedSSR,
  );
  const [isLoading, setIsLoading] = useState(true);
  const initialLoadRef = useRef(false);
  const initialized = useRef(false);

  // Initialize all refs early for use in all functions
  const activeTabRef = useRef(activeTab);
  const seatDataRef = useRef(seatData);
  const flightSearchRef = useRef(flightSearch);
  const ssrInfoDataRef = useRef(ssrInfoData);
  const dispatchRef = useRef(dispatch);
  const getSeatMapRef = useRef(getSeatMap);
  const selectedBucketRef = useRef(selectedBucket);
  const routerRef = useRef(router);
  const toastRef = useRef(toast);

  useEffect(() => {
    activeTabRef.current = activeTab;
    seatDataRef.current = seatData;
    flightSearchRef.current = flightSearch;
    ssrInfoDataRef.current = ssrInfoData;
    dispatchRef.current = dispatch;
    getSeatMapRef.current = getSeatMap;
    selectedBucketRef.current = selectedBucket;
    routerRef.current = router;
    toastRef.current = toast;
  }, [
    activeTab,
    seatData,
    flightSearch,
    ssrInfoData,
    dispatch,
    getSeatMap,
    selectedBucket,
    router,
    toast,
  ]);

  useEffect(() => {
    if (!isApiCall && !initialLoadRef.current) {
      initialLoadRef.current = true;
      let storedSeatData = [];
      let storedSSRInfoData = [];
      try {
        const seatDataStr = sessionStorage.getItem('seatData');
        const ssrInfoDataStr = sessionStorage.getItem('ssrInfoData');

        if (seatDataStr && seatDataStr !== 'undefined') {
          storedSeatData = JSON.parse(seatDataStr);
        }
        if (ssrInfoDataStr && ssrInfoDataStr !== 'undefined') {
          storedSSRInfoData = JSON.parse(ssrInfoDataStr);
        }
      } catch (error) {
        console.error('Error parsing stored data:', error);
      }
      if (storedSeatData?.length > 0 && storedSSRInfoData?.length > 0) {
        setIsApiCall(true);
        setSeatData(storedSeatData);
        setSsrInfoData(storedSSRInfoData);
        setIsLoading(false);
        const passenger = flightSearch.passenger;
        setPassengerData(passenger);
        // Don't set activeTab here - let the initialization logic handle it
        // SSR data processing will be handled by separate useEffect
      } else {
        setIsApiCall(true);
        apiCall();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    // If switching to seat tab, ensure seatMap is initialized for current flight segment
    if (tab === 'seat' && seatData?.length > 0 && ssrInfoData?.length > 0) {
      const currentSSRData =
        ssrInfoData[activeIndexes.i]?.[activeIndexes.index];
      if (currentSSRData) {
        handleSSR(
          currentSSRData,
          activeIndexes.i,
          activeIndexes.index,
          activeIndexes.paxIndex,
        );
      }
    }
  };

  // useEffect(() => {
  // if (seatData?.length > 0) {
  //   setActiveTab('seat');
  // }
  // if (ssrInfoData?.length > 0) {
  //   handleSSR(
  //     ssrInfoData?.[0]?.[0],
  //     activeIndexes?.i,
  //     activeIndexes?.index,
  //     activeIndexes?.paxIndex,
  //   );
  // }

  // if (meal?.length == 0 && baggage?.length == 0) {
  //   router.push('/review');
  // }
  // }, [seatData]);

  const formatInternationalSSRData = (
    ssrData: any,
    origin: string,
    destination: string,
  ) => {
    return ssrData.map((group: any) => {
      return group.filter((item: any) => {
        return (
          item.meal[0]?.origin === origin ||
          item.meal[0]?.destination === destination
        );
      });
    });
  };

  const formatSSRData = useCallback((ssrData: any) => {
    const fs = flightSearchRef.current;
    if (
      fs.travelMode == 'IN' &&
      fs.travelType == 'RT' &&
      fs.tripType == 'INRT'
    ) {
      let formattedSSRData = formatInternationalSSRData(
        ssrData,
        fs.origin,
        fs.destination,
      ).concat(formatInternationalSSRData(ssrData, fs.destination, fs.origin));
      ssrData = formattedSSRData;
      // ssrData = JSON.stringify(ssrData, null, 2);

      setSsrInfoData(ssrData);

      sessionStorage.setItem(
        'ssrInfoData',
        JSON.stringify(ssrData?.length > 0 ? [...ssrData] : []),
      );
      return ssrData.map((segment: any) =>
        segment.map((journey: any) => ({
          meal: {},
          seat: {},
          baggage: {},
        })),
      );
    }

    return ssrData.map((segment: any) =>
      segment.map((journey: any) => ({
        meal: {},
        seat: {},
        baggage: {},
      })),
    );
  }, []);

  // Define parseFlightString first (needed by handleSSR)
  const parseFlightString = (flightString: string) => {
    if (!flightString) {
      return {
        origin: '',
        destination: '',
        originCode: '',
        destinationCode: '',
        date: '',
        time: null,
      };
    }
    const cleaned = flightString.replace(/(\([A-Z]{3})\s*\)?/g, '($1)');

    const [origin, rest] = cleaned.split(' - ');
    const parts = rest.split(' | ');

    let destination = '';
    let date = '';
    let time: string | null = null;

    if (parts.length === 2) {
      [destination, date] = parts;
    } else if (parts.length === 3) {
      [destination, date, time] = parts;
    }

    const originCode = origin.match(/\(([A-Z]{3})\)/)?.[1];
    const destinationCode = destination.match(/\(([A-Z]{3})\)/)?.[1];

    return {
      origin: origin.trim(),
      destination: destination.trim(),
      originCode,
      destinationCode,
      date: date.trim(),
      time: time?.trim() ?? null,
    };
  };

  /** Seat API: [{ "bookingId_i_j": { sData, sInfo } }, ...] */
  const getSeatMapFromCurrentData = (
    currentSeatData: any[],
    segmentI: number,
    segmentIndex: number,
    sectorInfo?: string,
    apiBookingIds?: any[],
  ) => {
    if (!currentSeatData?.length) return null;

    const suffix = `_${segmentI}_${segmentIndex}`;
    for (const entry of currentSeatData) {
      if (!entry || typeof entry !== 'object') continue;
      if (entry.sInfo || entry.sData) return entry;
      for (const [key, value] of Object.entries(entry)) {
        if (key.endsWith(suffix) && value && typeof value === 'object') {
          return value;
        }
      }
    }

    const flightString = parseFlightString(sectorInfo ?? '');
    const origin = flightString?.originCode?.trim()?.toUpperCase();
    const destination = flightString?.destinationCode?.trim()?.toUpperCase();
    if (apiBookingIds?.length && origin && destination) {
      const matched = apiBookingIds.find(
        (item: any) =>
          item.origin?.trim()?.toUpperCase() === origin &&
          item.destination?.trim()?.toUpperCase() === destination,
      );
      const bookingId = String(
        matched?.bookingId ?? matched?.booking_id ?? matched?.id ?? '',
      );
      if (bookingId) {
        for (const entry of currentSeatData) {
          for (const [key, value] of Object.entries(entry || {})) {
            if (key.startsWith(bookingId) && value && typeof value === 'object') {
              return value;
            }
          }
        }
      }
    }

    const legacy = currentSeatData[segmentI]?.[segmentIndex];
    if (legacy?.sInfo || legacy?.sData) return legacy;

    const row = currentSeatData[segmentI];
    if (row && typeof row === 'object') {
      const first = Object.values(row)[0] as any;
      if (first?.sInfo || first?.sData) return first;
    }

    for (const entry of currentSeatData) {
      const first = Object.values(entry || {})[0] as any;
      if (first?.sInfo || first?.sData) return first;
    }
    return null;
  };

  const callBack = useCallback(() => {
    const fs = flightSearchRef.current;
    let updatePax: any = passengerData;

    // Calculate individual SSR amounts for each passenger
    const passengerAmounts = calculatePassengerAmounts(fs.selectedSSR);

    // Extract SSR items for each passenger
    const passengerSSRItems = extractPassengerSSRItems(fs.selectedSSR);

    updatePax = updatePax.map((data: any, index: number) => {
      const amounts = passengerAmounts[index] || {
        seat: 0,
        meal: 0,
        baggage: 0,
        total: 0,
      };

      const ssrItems = passengerSSRItems[index] || {
        seat: [],
        meal: [],
        baggage: [],
      };

      return {
        ...data,
        ssrInfo: fs.selectedSSR?.[index] || [],
        seatAmount: amounts.seat,
        mealAmount: amounts.meal,
        baggageAmount: amounts.baggage,
        totalSSRAmount: amounts.total,
        seat: ssrItems.seat,
        meal: ssrItems.meal,
        baggage: ssrItems.baggage,
      };
    });

    const ssr = {
      seat: 0,
      meal: 0,
      baggage: 0,
    };

    const updatedSSR = { ...ssr };

    // Calculate totals while preserving passenger-wise structure
    fs.selectedSSR?.forEach((passengerData: any) => {
      passengerData.forEach((flight: any) => {
        flight.forEach((segment: any) => {
          if (segment?.seat?.active && segment?.seat?.amount) {
            updatedSSR.seat += segment.seat.amount;
          }
          if (segment?.meal?.count && segment?.meal?.amount) {
            updatedSSR.meal += segment.meal.count * segment.meal.amount;
          }
          if (segment?.baggage?.count && segment?.baggage?.amount) {
            updatedSSR.baggage +=
              segment.baggage.count * segment.baggage.amount;
          }
        });
      });
    });

    const updatedFareDetails = {
      ...fs.fareDetails,
      fareDetails: {
        ...fs.fareDetails?.fareDetails,
        ...updatedSSR,
      },
    };

    updatedFareDetails.fareDetails.totalAmount =
      updatedFareDetails.fareDetails?.totalAmount +
      (updatedSSR?.seat || 0) +
      (updatedSSR?.meal || 0) +
      (updatedSSR?.baggage || 0);

    dispatchRef.current(updateFareDetails(updatedFareDetails));

    dispatchRef.current(updatePassenger(updatePax));

    routerRef.current.push('/review');
  }, [passengerData]);

  // Use refs to avoid re-creating handleSSR - CORE FIX FOR INFINITE LOOP
  // Refs are declared at the top of the component

  const handleSSR = useCallback(
    (
      data: any = ssrInfoDataRef.current?.[0]?.[0],
      i: number = 0,
      index: number = 0,
      paxIndex: number = 0,
    ) => {
      if (data) {
        setActiveIndexes({ i, index, paxIndex });

        // Initialize seatMap if seat data exists - run regardless of active tab
        // Check ref first (updated via useEffect), fallback to checking if we need to wait
        // When loading from sessionStorage, the ref might not be updated immediately
        const currentSeatData = seatDataRef.current;

        if (currentSeatData?.length > 0) {
          const seatMapData = getSeatMapFromCurrentData(
            currentSeatData,
            i,
            index,
            data?.sector_info,
            flightSearchRef.current.api_booking_id,
          );
          if (seatMapData) {
            const sInfo = seatMapData.sInfo;
            setSeatMap({
              ...seatMapData,
              sInfo: Array.isArray(sInfo) ? sInfo : Object.values(sInfo || {}),
              sData: seatMapData.sData || {},
            });
          } else {
            setSeatMap({ sInfo: [], sData: {} });
          }
        }

        // Update meal and baggage states
        if (data) {
          // Only update meal state if it's actually different
          const newMeal = data?.meal && data.meal.length > 0 ? data.meal : [];
          setMeal((prevMeal: any) => {
            // Compare arrays by length and reference
            if (JSON.stringify(prevMeal) !== JSON.stringify(newMeal)) {
              return newMeal;
            }
            return prevMeal;
          });

          // Only update baggage state if it's actually different
          const newBaggage =
            data?.baggage && data.baggage.length > 0 ? data.baggage : [];
          setBaggage((prevBaggage: any) => {
            // Compare arrays by length and reference
            if (JSON.stringify(prevBaggage) !== JSON.stringify(newBaggage)) {
              return newBaggage;
            }
            return prevBaggage;
          });
        }
      } else {
        // If no data, try to find first available SSR type
        const ssrArr: string[] = ['seat', 'meal', 'baggage'];
        const setterMap = {
          seat: setSeatData,
          meal: setMeal,
          baggage: setBaggage,
        };

        for (const item of ssrArr) {
          const value = data?.[item]?.length > 0 ? data[item] : [];
          setterMap[item as keyof typeof setterMap](value);

          if (data?.[item]?.length > 0) {
            setActiveTab(item);
            break;
          }
        }
      }
    },
    [], // NO DEPENDENCIES - function is completely stable!
  );

  const handleNameChange = (index: number) => {
    handleSSR(
      ssrInfoData?.[activeIndexes?.i]?.[activeIndexes?.index]?.[index],
      activeIndexes?.i,
      activeIndexes?.index,
      index,
    );
  };

  // Update fare details when selectedSSR changes (debounced to prevent rerenders)
  useEffect(() => {
    if (selectedSSR?.length > 0 && !isLoading) {
      const fs = flightSearchRef.current;

      const ssr = {
        seat: 0,
        meal: 0,
        baggage: 0,
      };

      const updatedSSR = { ...ssr };

      // Calculate totals while preserving passenger-wise structure
      selectedSSR?.forEach((passengerData: any) => {
        passengerData.forEach((flight: any) => {
          flight.forEach((segment: any) => {
            if (segment?.seat?.active && segment?.seat?.amount) {
              updatedSSR.seat += segment.seat.amount;
            }
            if (segment?.meal?.count && segment?.meal?.amount) {
              updatedSSR.meal += segment.meal.count * segment.meal.amount;
            }
            if (segment?.baggage?.count && segment?.baggage?.amount) {
              updatedSSR.baggage +=
                segment.baggage.count * segment.baggage.amount;
            }
          });
        });
      });

      // Only update if values changed to prevent unnecessary rerenders
      const currentSeat = fs.fareDetails?.fareDetails?.seat || 0;
      const currentMeal = fs.fareDetails?.fareDetails?.meal || 0;
      const currentBaggage = fs.fareDetails?.fareDetails?.baggage || 0;

      if (
        currentSeat !== updatedSSR.seat ||
        currentMeal !== updatedSSR.meal ||
        currentBaggage !== updatedSSR.baggage
      ) {
        const updatedFareDetails = {
          ...fs.fareDetails,
          fareDetails: {
            ...fs.fareDetails?.fareDetails,
            ...updatedSSR,
          },
        };

        // Calculate new total amount
        const baseAmount = updatedFareDetails.fareDetails?.totalAmount || 0;
        const oldAddOns = currentSeat + currentMeal + currentBaggage;
        const newAddOns =
          updatedSSR.seat + updatedSSR.meal + updatedSSR.baggage;

        updatedFareDetails.fareDetails.totalAmount =
          baseAmount - oldAddOns + newAddOns;

        dispatchRef.current(updateFareDetails(updatedFareDetails));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSSR, isLoading]);

  // Store refs for apiCall to avoid recreating it
  // Refs are declared at the top of the component

  const apiCall = useCallback(async () => {
    const ssr: Array<any> = flightSearchRef.current.ssrInfo;
    setSsrInfoData(ssr?.length > 0 ? [...ssr] : []);
    sessionStorage.setItem(
      'ssrInfoData',
      JSON.stringify(ssr?.length > 0 ? [...ssr] : []),
    );
    let ssrData: any = [];

    let passenger = flightSearchRef.current.passenger;

    setPassengerData(passenger);

    passenger.map((data: any, paxIndex: number) => {
      ssrData.push([]);
      ssrData[paxIndex] = formatSSRData(ssr);
    });
    const updatedSSR = {
      ...selectedBucketRef.current,
      seat: 0,
      meal: 0,
      baggage: 0,
      addOns: 0,
    };

    dispatchRef.current(updateFareDetails(updatedSSR));

    dispatchRef.current(updateSelectedSSR(ssrData));
    const seatParams = {
      bookingId: {
        bookingId: flightSearchRef.current.api_booking_id,
        passenger: flightSearchRef.current.passenger,
      },
    };

    dispatchRef.current(
      updateReviewData({
        api_booking_id: flightSearchRef.current.api_booking_id,
      }),
    );

    const seatMapResponse: any = await getSeatMapRef
      .current({
        data: seatParams,
      })
      .unwrap();

    const seatMapData = seatMapResponse.data;

    setSeatData(seatMapData);
    setIsLoading(false);
    sessionStorage.setItem('seatData', JSON.stringify(seatMapData));

    // Don't force setActiveTab here - let the tab initialization logic handle it
  }, [formatSSRData]);

  // Single init effect: runs once when API/cache load completes.
  // Reads source data directly (ssrInfoData[0][0]) so there are no stale-state
  // timing issues — derived states (meal/baggage) haven't been set yet at this point.
  useEffect(() => {
    if (!isApiCall || isLoading || initialized.current) return;
    initialized.current = true;

    const firstSSRData = ssrInfoData?.[0]?.[0];
    const hasSeat = (seatData?.length ?? 0) > 0;
    const hasMeal = (firstSSRData?.meal?.length ?? 0) > 0;
    const hasBaggage = (firstSSRData?.baggage?.length ?? 0) > 0;

    if (!hasSeat && !hasMeal && !hasBaggage) {
      routerRef.current.push('/review');
      toastRef.current({
        title: 'Information',
        description: 'No SSR found',
        variant: 'info',
      });
      return;
    }

    // Populate meal/baggage state and seatMap from source data
    if (firstSSRData) {
      handleSSR(firstSSRData, 0, 0, 0);
    }

    // Set active tab by priority: seat → meal → baggage
    if (hasSeat) setActiveTab('seat');
    else if (hasMeal) setActiveTab('meal');
    else setActiveTab('baggage');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isApiCall]);

  return isLoading ? (
    <div className="flex h-screen items-center justify-center bg-white">
      <img src={Loader} alt="loader" width={100} className="h-64 w-64" />
    </div>
  ) : (
    <div className="bg-tertiary">
      <div className="h-14 w-full bg-primary sm:h-20 lg:h-48" />
      <div className="absolute inset-x-0 top-14 mx-auto px-1 pb-4 sm:top-14 sm:w-[1200px] sm:pb-8 lg:top-32 lg:pb-20">
        <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
          <h2 className="pl-2 text-2xs font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
            SSR Details
          </h2>
        </div>
        <div className="flex w-full flex-col gap-4 lg:flex-row">
          <div className="h-full w-full rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:w-3/4 lg:p-8">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-wrap gap-4 sm:gap-6 lg:space-x-8">
                {[
                  ...(seatData?.length > 0 ? ['seat'] : []),
                  ...(meal?.length > 0 ? ['meal'] : []),
                  ...(baggage?.length > 0 ? ['baggage'] : []),
                ].map((tab) => (
                  <button
                    key={tab}
                    data-tab={tab}
                    onClick={() => handleTabClick(tab)}
                    className={`text-2xs text-gray-500 sm:text-sm ${
                      activeTab === tab
                        ? 'border-b-2 border-primary text-primary'
                        : ''
                    } font-medium`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              {/* <Link
                href="/payment"
                className="text-xs text-gray-500 hover:text-primary hover:underline"
              >
                Skip to Payment
              </Link> */}
            </div>

            <div className="my-4 flex flex-col gap-3 sm:my-5 sm:gap-4">
              <Tabs
                value={activeIndexes.i + '-' + activeIndexes.index}
                onValueChange={(value) => {
                  const [i, index] = value.split('-').map(Number);
                  const ssrVal = ssrInfoData?.[i]?.[index];
                  if (ssrVal) {
                    handleSSR(ssrVal, i, index, activeIndexes.paxIndex);
                  }
                }}
                className="w-full rounded-lg sm:w-fit"
              >
                <TabsList className="flex w-full flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4">
                  {ssrInfoData?.map((ssr: any, i: number) =>
                    ssr?.map((ssrVal: any, index: number) => {
                      const { originCode, destinationCode } = parseFlightString(
                        ssrVal?.sector_info,
                      );
                      return (
                        <TabsTrigger
                          key={`${i}-${index}`}
                          value={i + '-' + index}
                          className="items-center rounded-2xl bg-light px-2 py-0 text-2xs transition-all duration-500 hover:scale-95 data-[state=active]:bg-primary data-[state=active]:text-text_foreground sm:px-3 sm:py-1 sm:text-sm lg:px-4 lg:py-1"
                        >
                          {originCode} - {destinationCode}
                        </TabsTrigger>
                      );
                    }),
                  )}
                </TabsList>
              </Tabs>

              <Tabs
                value={activeIndexes?.paxIndex?.toString()}
                onValueChange={(value) => {
                  const index = parseInt(value, 10);
                  if (!isNaN(index)) {
                    handleNameChange(index);
                  }
                }}
              >
                <TabsList className="flex w-full flex-wrap gap-2 sm:justify-center md:justify-start lg:justify-start">
                  {passengerData?.map((pax: any, index: number) => (
                    <React.Fragment key={index}>
                      {pax.pax_type != 'INF' && (
                        <TabsTrigger
                          value={index.toString()}
                          className="max-w-[150px] truncate border-b-2 text-2xs data-[state=active]:border-primary sm:max-w-none sm:text-sm"
                        >
                          {pax?.title +
                            ' ' +
                            pax?.firstName +
                            ' ' +
                            pax?.lastName}
                        </TabsTrigger>
                      )}
                    </React.Fragment>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            <div className="mt-4">
              {activeTab === 'seat' && seatData?.length > 0 && (
                <Seat
                  seatMap={seatMap}
                  activeIndexes={activeIndexes}
                  handleSeat={handleSSR}
                />
              )}
              {activeTab === 'meal' && (
                <Meal
                  meal={meal}
                  activeIndexes={activeIndexes}
                  type="meal"
                  data={meal}
                  selected={() => {}}
                />
              )}
              {activeTab === 'baggage' && (
                <Baggage baggageData={baggage} activeIndexes={activeIndexes} />
              )}
            </div>
          </div>
          <div className="mt-4 flex w-full flex-col gap-5 lg:mt-0 lg:w-1/4">
            <div>
              <FareSummary
                summary={selectedBucket}
                callback={callBack}
                addOns={{ isShow: true }}
              />
            </div>
            <div>
              <BookingSummary />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SsrDetails;
